import type { VerifyLocationRequest, VerifyLocationResponse, VerifyOutcome } from '@src/pages/api/braze/types';

export const VERIFY_ENDPOINT = '/api/braze/verify-location';

/**
 * Delay before each attempt, in ms. Five attempts, ~31s of wall clock.
 *
 * The first attempt waits 1.5s on purpose: Braze's ingestion pipeline needs a
 * beat after the SDK flush, so firing at t=0 is a guaranteed wasted call
 * against a rate-limited endpoint.
 */
export const RETRY_SCHEDULE_MS = [1500, 2500, 4000, 8000, 15_000];

/** Per-attempt ceiling, so one stalled request cannot eat the whole budget. */
const ATTEMPT_TIMEOUT_MS = 5000;

export type VerifyBrazeLocationArgs = VerifyLocationRequest & {
	signal?: AbortSignal;
	schedule?: number[];
};

class AbortedError extends Error {}

/** A sleep that resolves early and cleans up its timer when the signal aborts. */
const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
	new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(new AbortedError());
			return;
		}

		const timer = setTimeout(() => {
			signal?.removeEventListener('abort', onAbort);
			resolve();
		}, ms);

		function onAbort() {
			clearTimeout(timer);
			reject(new AbortedError());
		}

		signal?.addEventListener('abort', onAbort, { once: true });
	});

const requestVerification = async (
	body: VerifyLocationRequest,
	signal?: AbortSignal
): Promise<VerifyLocationResponse | null> => {
	// Abort on either the caller's signal or our own per-attempt timeout.
	// Hand-rolled rather than AbortSignal.any/timeout, which need Chrome 116+
	// and Safari 17.4+ and would silently throw for a slice of real traffic.
	const attemptController = new AbortController();
	const timer = setTimeout(() => attemptController.abort(), ATTEMPT_TIMEOUT_MS);
	const onAbort = () => attemptController.abort();
	signal?.addEventListener('abort', onAbort, { once: true });

	try {
		const response = await fetch(VERIFY_ENDPOINT, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
			signal: attemptController.signal,
		});

		if (!response.ok) {
			return null;
		}
		return (await response.json()) as VerifyLocationResponse;
	} catch {
		if (signal?.aborted) {
			throw new AbortedError();
		}
		// Our own route is down or timed out. Treat as retryable.
		return null;
	} finally {
		clearTimeout(timer);
		signal?.removeEventListener('abort', onAbort);
	}
};

/**
 * What a single response tells us to do next: either a terminal outcome, or
 * keep polling. `retry-after-error` also keeps polling, but remembers that the
 * attempt failed for real so a later timeout reports as an error.
 */
type AttemptVerdict = VerifyOutcome | 'retry' | 'retry-after-error';

const classifyAttempt = (result: VerifyLocationResponse): AttemptVerdict => {
	if (result.verified) {
		return 'verified';
	}

	if (result.reason === 'mismatch') {
		return 'mismatch';
	}

	if (result.reason === 'not-configured') {
		return 'not-configured';
	}

	if (!result.retryable) {
		// e.g. invalid-request. Retrying cannot change the answer.
		return 'error';
	}

	return result.reason === 'upstream-error' ? 'retry-after-error' : 'retry';
};

/**
 * Poll our verification route until Braze confirms it holds the coordinates we
 * sent, or until the attempt budget runs out.
 *
 * Resolves rather than throws in every case, so the caller can branch on the
 * outcome without a try/catch.
 */
export const verifyBrazeLocation = async ({
	deviceId,
	latitude,
	longitude,
	signal,
	schedule = RETRY_SCHEDULE_MS,
}: VerifyBrazeLocationArgs): Promise<VerifyOutcome> => {
	let sawError = false;

	try {
		for (const scheduledDelay of schedule) {
			await sleep(scheduledDelay, signal);

			const result = await requestVerification({ deviceId, latitude, longitude }, signal);

			if (result === null) {
				sawError = true;
				continue;
			}

			const verdict = classifyAttempt(result);

			if (verdict === 'retry-after-error') {
				sawError = true;
			} else if (verdict !== 'retry') {
				return verdict;
			}

			if (result.retryAfterMs && result.retryAfterMs > 0) {
				// Honour the server's backpressure on top of the schedule.
				await sleep(result.retryAfterMs, signal);
			}
		}
	} catch (error) {
		if (error instanceof AbortedError) {
			return 'aborted';
		}
		return 'error';
	}

	return sawError ? 'error' : 'timeout';
};
